import { Alert, AlertProps, AlertTitle, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import React, { forwardRef, ReactNode } from 'react';

import { COLORS } from '../../../theme/colors';
import { styleConfigs } from '../../resources/styleConfigs';

export type AtomAlertSeverity = 'success' | 'error' | 'warning' | 'info';
export type AtomAlertVariant = 'filled' | 'outlined' | 'standard';

export interface IAtomAlertProps extends Omit<AlertProps, 'severity' | 'variant'> {
  severity?: AtomAlertSeverity;
  variant?: AtomAlertVariant;
  title?: string;
  closable?: boolean;
  customSx?: SxProps;
  children?: ReactNode;
  leftIcon?: ReactNode;
  linkText?: string;
  onLinkClick?: () => void;
}

const StyledAlert = styled(Alert)<{ ownerState: { variant: AtomAlertVariant } }>(({ theme, ownerState }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  position: 'relative',
  '& .MuiAlert-message': {
    fontWeight: ownerState.variant === 'filled' ? 500 : 400,
    padding: styleConfigs.marginPadding.empty,
    display: 'flex',
    flexDirection: 'column',
    width: styleConfigs.dimensions.percent100,
  },
  '& .MuiAlert-action': {
    position: 'absolute',
    top: styleConfigs.marginPadding.custom8,
    right: styleConfigs.marginPadding.custom8,
    padding: styleConfigs.marginPadding.empty,
  },
  '&.MuiAlert-filledSuccess .MuiAlert-icon svg path': {
    fill: COLORS.emeraldFern,
  },
  '&.MuiAlert-filledError .MuiAlert-icon svg path': {
    fill: COLORS.iconMenuEditCollection,
  },
}));

const AtomAlert = forwardRef<HTMLDivElement, IAtomAlertProps>(
  (
    {
      severity = 'info',
      variant = 'standard',
      title,
      closable = true,
      customSx,
      children,
      onClose,
      leftIcon,
      linkText,
      onLinkClick,
      ...rest
    },
    ref,
  ) => {
    const closeProps = closable ? { onClose: onClose ?? (() => undefined) } : {};

    return (
      <StyledAlert
        ref={ref}
        severity={severity}
        variant={variant}
        ownerState={{ variant }}
        sx={customSx}
        icon={leftIcon}
        {...closeProps}
        {...rest}
      >
        {title && <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>}
        <Box sx={{ alignSelf: 'flex-start', width: styleConfigs.dimensions.percent100 }}>{children}</Box>
        {linkText && onLinkClick && (
          <Box
            component="button"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onLinkClick();
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onLinkClick();
              }
            }}
            sx={{
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              marginTop: styleConfigs.marginPadding.custom4,
              fontWeight: styleConfigs.fonts.weight500,
              fontSize: styleConfigs.fonts.customFontResponsive,
              lineHeight: styleConfigs.fonts.lineHeight22,
              letterSpacing: styleConfigs.fonts.letterSpacing046,
              color: severity === 'error' ? COLORS.crimsonRed : COLORS.emeraldFern,
              display: 'block',
              width: styleConfigs.dimensions.percent100,
              textAlign: 'right',
            }}
          >
            {linkText}
          </Box>
        )}
      </StyledAlert>
    );
  },
);

AtomAlert.displayName = 'AtomAlert';

export default AtomAlert;
export { AtomAlert };
